/**
 * Zoomo Rides design tokens — one set per theme, read from the owner's mockups
 * (docs/mockups/customer-app-mockups.html: every screen declares its colours as CSS variables).
 *
 * Brand: Night #0D0D0D + Aqua #5CE1E6 (dark); white + deep aqua #08737C / #1FB5C0 (light). The Z logo is never recoloured.
 *
 * Two surfaces: `base` (sign-in, home, lists) and `map` (screens drawn over a map, whose cards sit one step lighter
 * in dark mode and whose illustrations use the map strip's colours).
 */
export type Mode = 'dark' | 'light';
export type Surface = 'base' | 'map';
export type Tokens = Record<string, string>;

const DARK: Tokens = {
  // surfaces
  bg: '#0D0D0D', s1: '#161616', s2: '#1F1F1F', s3: '#2A2A2A', sheet: '#131313', float: '#1B1B1B',
  line: 'rgba(255,255,255,0.08)', line2: 'rgba(255,255,255,0.16)',
  // ink
  ink: '#F5F5F3', ink2: '#A6A6A3', ink3: '#8C8C89',
  // aqua
  ac: '#5CE1E6', acText: '#5CE1E6', acSoft: 'rgba(92,225,230,0.13)', acInk: '#0D0D0D', asked: 'rgba(92,225,230,0.28)',
  pri: '#5CE1E6', priInk: '#0D0D0D', sel: '#5CE1E6', on: '#5CE1E6', focus: '#5CE1E6', star: '#5CE1E6',
  chipOn: '#5CE1E6', chipOnInk: '#0D0D0D', tagLine: 'rgba(92,225,230,0.55)',
  heroA: '#123B3E', heroB: '#141B1C', heroLine: 'rgba(92,225,230,0.16)',
  danger: '#D92D20', dangerSoft: 'rgba(217,45,32,0.14)', sosRing: 'rgba(13,13,13,0.9)',
  // map + route
  route: '#5CE1E6', routeCase: '#0D0D0D', routeDone: '#6E7373', mapLabel: '#7C807F', you: '#5CE1E6',
  pinBg: '#5CE1E6', pinInk: '#0D0D0D', dim: 'rgba(13,13,13,0.35)', mapBg: '#101211',
  // glass tab bar
  glass: 'rgba(30,30,30,0.84)', glassLine: 'rgba(255,255,255,0.09)', navShadow: '0 12px 32px rgba(0,0,0,0.55)',
  navOn: '#5CE1E6', navOnInk: '#0D0D0D', navOff: '#A6A6A3',
  // elevation
  shadow: '0 0 0 1px rgba(255,255,255,0.07), 0 8px 24px rgba(0,0,0,0.5)',
  sheetShadow: '0 -1px 0 rgba(255,255,255,0.07)',
  scrim: 'rgba(0,0,0,0.55)',
  panelShadow: '0 0 0 1px rgba(255,255,255,0.07), 0 18px 48px rgba(0,0,0,0.55)',
  vehEdge: 'rgba(255,255,255,0.16)', vehShadow: 'rgba(0,0,0,0.4)',
  // number pad
  kb: '#1A1A1B', key: '#434345', key2: '#2B2B2D', keyInk: '#FFFFFF', keyShadow: 'rgba(0,0,0,0.45)',
  // illustration (hills, river, village, night sky)
  h1: '#141B1C', h2: '#182324', h3: '#1D2B2C', mh1: '#1E292A', mh2: '#243536', mh3: '#2B4244',
  river: '#123B3E', riverHi: '#5CE1E6', road: '#1E2021', roadDash: '#4A4E50', snow: '#33494B', tree: '#1E3436',
  tree2: '#224042', house: '#203133', roof: '#2C4A4C', win: '#5CE1E6', fig: '#4A5759', bird: '#4F6669',
  skyStar: '#F5F5F3', logoEdge: 'rgba(255,255,255,0.16)', track: '#0D0D0D',
};

const LIGHT: Tokens = {
  bg: '#FFFFFF', s1: '#F4F5F5', s2: '#EBEDED', s3: '#DDE1E1', sheet: '#FFFFFF', float: '#FFFFFF',
  line: 'rgba(13,13,13,0.08)', line2: 'rgba(13,13,13,0.14)',
  ink: '#0D0D0D', ink2: '#575B5B', ink3: '#5F6464',
  ac: '#5CE1E6', acText: '#08737C', acSoft: '#D5F4F6', acInk: '#0D0D0D', asked: '#BFEDEF',
  pri: '#0D0D0D', priInk: '#FFFFFF', sel: '#0D0D0D', on: '#0D0D0D', focus: '#0D0D0D', star: '#1FB5C0',
  chipOn: '#0D0D0D', chipOnInk: '#FFFFFF', tagLine: '#08737C',
  heroA: '#D5F4F6', heroB: '#F2FCFC', heroLine: 'rgba(8,115,124,0.10)',
  danger: '#D92D20', dangerSoft: 'rgba(217,45,32,0.10)', sosRing: '#FFFFFF',
  route: '#0D0D0D', routeCase: '#FFFFFF', routeDone: '#8F9696', mapLabel: '#6A7070', you: '#1FB5C0',
  pinBg: '#0D0D0D', pinInk: '#FFFFFF', dim: 'rgba(255,255,255,0.35)', mapBg: '#F2F3F1',
  glass: 'rgba(255,255,255,0.86)', glassLine: 'rgba(13,13,13,0.06)',
  navShadow: '0 10px 30px rgba(13,13,13,0.14), 0 1px 3px rgba(13,13,13,0.08)',
  navOn: '#0D0D0D', navOnInk: '#FFFFFF', navOff: '#5F6464',
  shadow: '0 1px 2px rgba(13,13,13,0.08), 0 6px 18px rgba(13,13,13,0.10)',
  sheetShadow: '0 -8px 30px rgba(13,13,13,0.10)',
  scrim: 'rgba(13,13,13,0.32)',
  panelShadow: '0 1px 2px rgba(13,13,13,0.06), 0 16px 40px rgba(13,13,13,0.16)',
  vehEdge: 'rgba(13,13,13,0)', vehShadow: 'rgba(13,13,13,0.14)',
  kb: '#D3D6DB', key: '#FFFFFF', key2: '#ADB3BC', keyInk: '#0D0D0D', keyShadow: 'rgba(0,0,0,0.28)',
  h1: '#E6F6F7', h2: '#D2EFF1', h3: '#BEE8EB', mh1: '#DDF2F4', mh2: '#C9EBEE', mh3: '#B6E3E7',
  river: '#8EDCE1', riverHi: '#FFFFFF', road: '#2B2F30', roadDash: '#FFFFFF', snow: '#FFFFFF', tree: '#A5DADF',
  tree2: '#8DD0D6', house: '#E9F8F9', roof: '#8DD0D6', win: '#3FC9CF', fig: '#3A4749', bird: '#6E9599',
  skyStar: 'rgba(13,13,13,0)', logoEdge: 'rgba(13,13,13,0)', track: '#FFFFFF',
};

/** Map screens: cards one step lighter in dark, a brighter aqua for markers in light, the map strip's colours. */
const MAP: Record<Mode, Tokens> = {
  dark: {
    s1: '#1A1A1A', s2: '#222222', s3: '#2E2E2E', float: '#1C1C1C', acSoft: 'rgba(92,225,230,0.12)',
    river: '#10292C', snow: '#3D5658', tree: '#2A4446', tree2: '#2F4E50', house: '#2B4143', roof: '#3A5A5C', fig: '#5B6A6C', bird: '#5B7477',
  },
  light: {
    ac: '#1FB5C0', acSoft: 'rgba(92,225,230,0.24)',
    river: '#9FDDE2', tree: '#9CD4D9', tree2: '#86CAD0', house: '#F2FBFB', roof: '#86CAD0', fig: '#2E3A3C',
  },
};

export function colours(mode: Mode, surface: Surface = 'base'): Tokens {
  const base = mode === 'dark' ? DARK : LIGHT;
  return surface === 'map' ? { ...base, ...MAP[mode] } : base;
}

/** Spacing scale (px). The mockups use a 4-pt grid with 20 px screen gutters. */
export const space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 28, 8: 32, gutter: 20 } as const;

/** Corner radii from the mockups. */
export const radius = { chip: 14, field: 18, button: 18, card: 20, tile: 24, hero: 28, sheet: 28, pill: 999 } as const;

/**
 * Type scale (size / line height / weight / tracking), Satoshi. Names follow where the mockups use them.
 */
export const type = {
  display: { fontSize: 44, lineHeight: '46px', fontWeight: 900, letterSpacing: '-0.035em' },
  title1: { fontSize: 38, lineHeight: '42px', fontWeight: 900, letterSpacing: '-0.04em' },
  title2: { fontSize: 32, lineHeight: '36px', fontWeight: 900, letterSpacing: '-0.03em' },
  title3: { fontSize: 24, lineHeight: '30px', fontWeight: 700, letterSpacing: '-0.02em' },
  headline: { fontSize: 22, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.015em' },
  section: { fontSize: 17, lineHeight: '22px', fontWeight: 700, letterSpacing: '-0.01em' },
  bodyStrong: { fontSize: 16, lineHeight: '22px', fontWeight: 700 },
  body: { fontSize: 16, lineHeight: '24px', fontWeight: 500 },
  row: { fontSize: 15, lineHeight: '20px', fontWeight: 700 },
  callout: { fontSize: 14, lineHeight: '20px', fontWeight: 500 },
  sub: { fontSize: 13, lineHeight: '18px', fontWeight: 500 },
  caption: { fontSize: 12.5, lineHeight: '16px', fontWeight: 500 },
  overline: { fontSize: 11, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.1em' },
} as const;

/** Motion: durations (ms) and curves. Transform/opacity only; all of it is off with prefers-reduced-motion. */
export const motion = {
  tap: 120,
  fast: 180,
  base: 260,
  sheet: 320,
  glide: 1600,
  ease: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

/** Inline CSS variables for a theme + surface (set on each screen root). */
export function tokenVars(mode: Mode, surface: Surface = 'base'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(colours(mode, surface))) out[`--${k}`] = v;
  return out;
}
